import { Drawer, Typography, Form, Radio, Tabs, Row, Col } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { deliveryWindowsMocked } from './mockedSlots';
import Button from '../Button/Button';

interface FormValues {
  deliveryType: 'instant' | 'schedule';
  deliveryWindow: string;
}

export type DeliveryWindow = {
  pickupStart: string;
  pickupEnd: string;
  dropoffStart: string;
  dropoffEnd: string;
  id: string;
};

type DeliveryOptionDrawerProps = {
  onClose: () => void;
  isVisible: boolean;
  deliveryWindows?: DeliveryWindow[][];
};

const DeliveryOptionDrawer = ({ onClose, isVisible }: DeliveryOptionDrawerProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();

  const handleSubmit = () => {
    form
      .validateFields()
      .then(() => {
        // console.log('Form values: ', values);
        // Handle form submission here
      })
      .catch(() => {
        // console.log('Validation failed:', info);
      });
  };

  const formatTimestamp = (timestamp: string) => dayjs(timestamp).format('h:mm A');

  const tabLabels = ['Today', 'Tomorrow', 'Day After Tomorrow'];

  // const handleTabChange = (activeKey: string) => {
  //   // Reset form values when tab changes
  //   form.resetFields(['deliveryWindow']);
  // };

  return (
    <Drawer
      bodyStyle={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}
      title={
        <Typography.Title style={{ marginBottom: 0 }} level={2}>
          {t('deliveries.deliveryOptionsDrawer.title')}
        </Typography.Title>
      }
      onClose={onClose}
      open={isVisible}
    >
      <Form layout="vertical" form={form}>
        <Form.Item
          name="deliveryType"
          requiredMark={false}
          label={
            <Typography.Text strong type="secondary">
              {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.title')}
            </Typography.Text>
          }
          rules={[{ required: true }]}
        >
          <Radio.Group>
            <Radio value="instant">
              <Col style={{ padding: '16px 8px' }}>
                <Row>
                  <Col>
                    <Typography.Title level={4} data-testid="instant-title">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.instant')}
                    </Typography.Title>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Typography.Text type="secondary" data-testid="instant-description">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.instantDescription')}
                    </Typography.Text>
                  </Col>
                </Row>
              </Col>
            </Radio>
            <Radio value="schedule">
              <Col style={{ padding: '16px 8px' }}>
                <Row>
                  <Col>
                    <Typography.Title level={4} data-testid="schedule-title">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.schedule')}
                    </Typography.Title>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Typography.Text type="secondary" data-testid="schedule-description">
                      {t('deliveries.deliveryOptionsDrawer.deliveryTypeSection.scheduleDescription')}
                    </Typography.Text>
                  </Col>
                </Row>
              </Col>
            </Radio>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="deliveryWindow"
          rules={[{ required: true }]}
          requiredMark={false}
          label={
            <Typography.Text strong type="secondary">
              {t('deliveries.deliveryOptionsDrawer.deliveryTimeSection.title')}
            </Typography.Text>
          }
        >
          <Tabs
            type="card"
            style={{ marginTop: '16px' }}
            items={deliveryWindowsMocked.map((windowArray, tabIndex) => {
              return {
                label: tabLabels[tabIndex],
                key: `sTab${tabIndex}`,
                children: (
                  <Radio.Group style={{ display: 'flex', flexDirection: 'column', rowGap: '16px', marginTop: '16px' }}>
                    {windowArray.map(window => (
                      <Radio value={window.id} key={window.id}>
                        <Typography.Text>
                          {`${formatTimestamp(window.pickupStart)} - ${formatTimestamp(window.pickupEnd)} / ${formatTimestamp(window.dropoffStart)} - ${formatTimestamp(window.dropoffEnd)}`}
                        </Typography.Text>
                      </Radio>
                    ))}
                  </Radio.Group>
                )
              };
            })}
          />
        </Form.Item>
      </Form>
      <Button block type="primary" onClick={handleSubmit} data-testid="confirm-button">
        {t('deliveries.deliveryOptionsDrawer.confirm')}
      </Button>
    </Drawer>
  );
};

export default DeliveryOptionDrawer;
